import { createElement } from 'react';
import { cloneDeep, mapKeys, mapValues } from 'lodash';
import { i18n } from '@lingui/core';

import { getLogger } from '../../../utilities/UseLog';
import { ENTITY_TYPES } from '../../../services/EntityService';
import statusListPanelPreviewImage from '../../../static/images/status-list-panel-preview.png';
import logListPanelPreviewImage from '../../../static/images/log-list-panel-preview.png';
import areaChartPanelPreviewImage from '../../../static/images/area-chart-panel-preview.png';
import lineChartPanelPreviewImage from '../../../static/images/line-chart-panel-preview.png';
import statusChartPanelPreviewImage from '../../../static/images/status-chart-panel-preview.png';
import statusBreakdownChartPanelPreviewImage from '../../../static/images/status-breakdown-chart-panel-preview.png';
import statusBreakoutChartPanelPreviewImage from '../../../static/images/status-breakout-chart-panel-preview.png';
import columnChartPanelPreviewImage from '../../../static/images/column-chart-panel-preview.png';
import comparisonBreakdownPanelPreviewImage from '../../../static/images/comparison-breakdown-panel-preview.png';
import comparisonOverlayPanelPreviewImage from '../../../static/images/comparison-overlay-panel-preview.png';
import mapPanelPreviewImage from '../../../static/images/map-panel-preview.png';
import matrixPanelPreviewImage from '../../../static/images/matrix-panel-preview.png';

const log = getLogger('PanelRegistry');

const DEFAULT_PANEL_TYPE_DATA_TABLE = 'DataTable';
const FORM_PROVIDER = 'FormProvider';

export const DEFAULT_PANEL_FILTERS = {
    required: [],
    optional: [],
};

export const PANEL_CATEGORIES = {
    ...ENTITY_TYPES,
    ALERT: 'alert',
};

export const PANEL_PRIORITIES = {
    OVERVIEW: 0,
    LIST: 1,
    STATUS: 2,
    SUBLIST: 3,
    CHART: 4,
    OTHER: 5,
};

export const STATUS_LIST_PANEL_PREVIEW_IMAGE = statusListPanelPreviewImage;
export const LOG_LIST_PANEL_PREVIEW_IMAGE = logListPanelPreviewImage;
export const AREA_CHART_PANEL_PREVIEW_IMAGE = areaChartPanelPreviewImage;
export const LINE_CHART_PANEL_PREVIEW_IMAGE = lineChartPanelPreviewImage;
export const COLUMN_CHART_PANEL_PREVIEW_IMAGE = columnChartPanelPreviewImage;
export const STATUS_CHART_PANEL_PREVIEW_IMAGE = statusChartPanelPreviewImage;
export const STATUS_BREAKDOWN_CHART_PANEL_PREVIEW_IMAGE = statusBreakdownChartPanelPreviewImage;
export const STATUS_BREAKOUT_CHART_PANEL_PREVIEW_IMAGE = statusBreakoutChartPanelPreviewImage;
export const MAP_PANEL_PREVIEW_IMAGE = mapPanelPreviewImage;
export const MATRIX_PANEL_PREVIEW_IMAGE = matrixPanelPreviewImage;
export const COMPARISON_BREAKDOWN_PANEL_PREVIEW_IMAGE = comparisonBreakdownPanelPreviewImage;
export const COMPARISON_OVERLAY_PANEL_PREVIEW_IMAGE = comparisonOverlayPanelPreviewImage;

export function createPanelComponent(panelTypeName, props) {
    return createElement(PanelRegistry.getPanelType(panelTypeName), {
        ...props,
        typeName: panelTypeName,
    });
}

export default class PanelRegistry {
    // Maps the panel type name to the component type.
    static panelTypeMap = {};

    // Maps the panel type name to the filter requirements for the panel.
    static panelFilterMap = {};

    // Maps the panel categories (in the panel catalog) to the panel catalog entries in the category.
    static panelCategoryMap = mapValues(
        mapKeys(PANEL_CATEGORIES, (value) => value),
        () => []
    );

    static registerPanel(options, panelType) {
        const panelTypeName = options.typeAlias || panelType.name;

        if (PanelRegistry.panelTypeMap[panelTypeName]) log.warn(`Duplicate panel type "${panelTypeName}" registered`);

        // Add the panel to the type map.
        PanelRegistry.panelTypeMap[panelTypeName] = panelType;

        // Add the panel's filters to the filter map.
        PanelRegistry.panelFilterMap[panelTypeName] = options.filters;

        // If the panel is registered with an alias, add entries for the primary type name too.
        if (panelTypeName !== panelType.name) {
            PanelRegistry.panelTypeMap[panelType.name] = panelType;
            PanelRegistry.panelFilterMap[panelType.name] = options.filters;
        }

        // Add the panel's catalog entry to the category map.
        if (options.catalogEntry) {
            const { defaultProps } = panelType;
            const {
                category,
                title,
                description,
                previewImage,
                ipm = false,
                priority = PANEL_PRIORITIES.OTHER,
            } = options.catalogEntry;
            const categoryEntries = PanelRegistry.panelCategoryMap[category];

            if (!categoryEntries) throw new Error(`Unkown panel category "${category}"`);
            if (!title)
                throw new Error(
                    `A title is required when registering a panel with a category entry (${panelTypeName})`
                );
            if (!description)
                throw new Error(
                    `A description is required when registering a panel with a category entry (${panelTypeName})`
                );
            if (!previewImage)
                throw new Error(
                    `A preview image is required when registering a panel with a category entry (${panelTypeName})`
                );

            const existingEntry = categoryEntries.find((entry) => entry.typeName === panelTypeName);

            if (!existingEntry) {
                categoryEntries.push({
                    typeName: panelTypeName,
                    title: i18n._(title),
                    description: i18n._(description),
                    priority,
                    ipm,
                    previewImage,
                    defaultProps,
                });
            }
        }
    }

    static getPanelType(panelTypeName) {
        const panelType = PanelRegistry.panelTypeMap[panelTypeName];

        if (!panelType) throw new Error(`Unknown panel type "${panelTypeName}"`);

        return panelType;
    }

    static getPanelTypesByCategory(category) {
        return cloneDeep(PanelRegistry.panelCategoryMap[category]);
    }

    static getFiltersForPanelType(panelTypeName) {
        if (!panelTypeName) {
            return DEFAULT_PANEL_FILTERS;
        }
        const panelFilters = PanelRegistry.panelFilterMap[panelTypeName];

        if (!panelFilters && DEFAULT_PANEL_TYPE_DATA_TABLE !== panelTypeName && FORM_PROVIDER !== panelTypeName) {
            log.warn(`No filters defined for panel type "${panelTypeName}"`);
        }

        return cloneDeep(panelFilters || DEFAULT_PANEL_FILTERS);
    }
}
